import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErrorMessage } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable, Badge } from "@/components/common/DataTable";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Eye, Power, PauseCircle, Loader2 } from "lucide-react";

const statusColor = { Active: "green", Suspended: "amber", Archived: "slate", Trial: "blue", Expired: "red" };

export default function TenantsPage() {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ deploymentMode: "SaaS", planCode: "SILVER" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/v1/tenants");
      setRows(r.data.data.items);
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    api.get("/v1/plans").then((r) => setPlans(r.data.data)).catch(() => {});
  }, [load]);

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/v1/tenants", form);
      toast.success("Tenant created & onboarded");
      setOpen(false);
      setForm({ deploymentMode: "SaaS", planCode: "SILVER" });
      load();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const lifecycle = async (id, action) => {
    try {
      await api.patch(`/v1/tenants/${id}/${action}`);
      toast.success(`Tenant ${action}d`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const columns = [
    { key: "tenantCode", header: "Code", render: (r) => <span className="font-mono text-xs font-semibold">{r.tenantCode}</span> },
    { key: "tenantName", header: "Tenant" },
    { key: "industry", header: "Industry", render: (r) => r.industry || "—" },
    { key: "planCode", header: "Plan", render: (r) => <Badge color="teal">{r.planCode || "—"}</Badge> },
    { key: "status", header: "Status", render: (r) => <Badge color={statusColor[r.status] || "slate"}>{r.status}</Badge> },
    {
      key: "_actions", header: "",
      render: (r) => (
        <div className="flex items-center justify-end gap-1">
          <button onClick={() => navigate(`/admin/tenants/${r.id}`)} className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500" data-testid={`view-tenant-${r.id}`}>
            <Eye className="h-4 w-4" />
          </button>
          {hasPermission("TENANT.SUSPEND") && r.status === "Active" && (
            <button onClick={() => lifecycle(r.id, "suspend")} className="p-1.5 rounded-md hover:bg-amber-50 text-amber-500" title="Suspend"><PauseCircle className="h-4 w-4" /></button>
          )}
          {hasPermission("TENANT.ACTIVATE") && r.status !== "Active" && (
            <button onClick={() => lifecycle(r.id, "activate")} className="p-1.5 rounded-md hover:bg-emerald-50 text-emerald-500" title="Activate"><Power className="h-4 w-4" /></button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div data-testid="tenants-page">
      <PageHeader
        title="Tenant Management" subtitle="Create and govern tenant organizations"
        actions={hasPermission("TENANT.CREATE") && (
          <button onClick={() => setOpen(true)} className="inline-flex items-center gap-2 bg-primary text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-primary/90" data-testid="create-tenant-btn">
            <Plus className="h-4 w-4" /> New Tenant
          </button>
        )}
      />
      <DataTable columns={columns} rows={rows} loading={loading} testid="tenants-table" />

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="tenant-create-dialog">
            <h3 className="text-lg font-bold text-slate-900 mb-1">Onboard New Tenant</h3>
            <p className="text-sm text-slate-500 mb-4">Creates tenant, default company, admin user & subscription.</p>
            <div className="grid grid-cols-2 gap-3">
              {[
                ["tenantCode", "Tenant Code *", "ACME"],
                ["tenantName", "Tenant Name *", "Acme Corporation"],
                ["industry", "Industry", "Retail"],
                ["region", "Region", "IN"],
              ].map(([k, label, ph]) => (
                <div key={k}>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
                  <input value={form[k] || ""} onChange={(e) => set(k, e.target.value)} placeholder={ph}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/40" data-testid={`tenant-${k}`} />
                </div>
              ))}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Plan *</label>
                <select value={form.planCode} onChange={(e) => set("planCode", e.target.value)} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" data-testid="tenant-planCode">
                  {plans.map((p) => <option key={p.planCode} value={p.planCode}>{p.planName}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Deployment</label>
                <select value={form.deploymentMode} onChange={(e) => set("deploymentMode", e.target.value)} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" data-testid="tenant-deploymentMode">
                  {["SaaS", "PrivateCloud", "OnPrem"].map((d) => <option key={d}>{d}</option>)}
                </select>
              </div>
              <div className="col-span-2 border-t border-slate-100 pt-3 mt-1">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Tenant Admin</p>
              </div>
              {[["adminEmail", "Admin Email *", "admin@acme.com"], ["adminPassword", "Admin Password *", "min 12 chars"]].map(([k, label, ph]) => (
                <div key={k}>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
                  <input type={k === "adminPassword" ? "password" : "text"} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} placeholder={ph}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/40" data-testid={`tenant-${k}`} />
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm rounded-lg border border-slate-300 hover:bg-slate-50">Cancel</button>
              <button onClick={create} disabled={saving} className="px-4 py-2 text-sm rounded-lg bg-primary text-white font-semibold hover:bg-primary/90 flex items-center gap-2 disabled:opacity-60" data-testid="tenant-create-save">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} Create Tenant
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
